import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  SITE_URL, SITE_NAME, OG_IMAGE, DEFAULT_TITLE, DEFAULT_DESC, DEFAULT_KEYWORDS, SITUATION_KEYWORDS, SITUATION_IMAGES, PAGE_SEO,
  organizationLd, websiteLd, serviceLd, breadcrumbLd, articleLd, caseLd, faqLd,
} from "../../lib/seo";
import { GROWTH_SITUATIONS } from "../../lib/content";
import { useBlogs } from "../../lib/blogsApi";
import { useCases } from "../../lib/casesApi";

const clean = (p) => (p !== "/" && p.endsWith("/") ? p.slice(0, -1) : p);
const abs = (src) => (!src ? OG_IMAGE : src.startsWith("http") ? src : `${SITE_URL}${src}`);

export const AutoSeo = () => {
  const { pathname } = useLocation();
  const path = clean(pathname);
  const blogs = useBlogs();
  const cases = useCases();
  const findBlog = (slug) => blogs.find((b) => b.slug === slug);
  const findCase = (slug) => cases.find((c) => c.slug === slug);

  let meta = PAGE_SEO[path];
  let ogImage = OG_IMAGE;
  const crumbs = [{ name: "Home", path: "/" }];
  const ld = [organizationLd, websiteLd];

  if (!meta && path.startsWith("/what-we-solve/")) {
    const s = GROWTH_SITUATIONS.find((g) => clean(g.href) === path);
    if (s) {
      const slug = path.replace("/what-we-solve/", "");
      meta = { title: `${s.title}: What We Solve | SWITCH`, description: s.blurb || DEFAULT_DESC, keywords: SITUATION_KEYWORDS[slug] || DEFAULT_KEYWORDS, image: SITUATION_IMAGES[slug] };
      crumbs.push({ name: "What We Solve", path: "/what-we-solve" }, { name: s.title, path });
    }
  } else if (!meta && path.startsWith("/insights/")) {
    const b = findBlog(path.replace("/insights/", ""));
    if (b) {
      meta = { title: b.seoTitle || `${b.title} | Switchboard | SWITCH`, description: b.excerpt || DEFAULT_DESC, keywords: [...(b.tags || []), b.situation, "growth strategy"].filter(Boolean).join(", ") };
      ogImage = abs(b.seoImage || b.image);
      crumbs.push({ name: "Switchboard", path: "/insights" }, { name: `No. ${b.number}`, path });
      ld.push(articleLd(b, `${SITE_URL}${path}`));
    }
  } else if (!meta && path.startsWith("/our-work/")) {
    const c = findCase(path.replace("/our-work/", ""));
    if (c) {
      meta = { title: c.seoTitle || `${c.headline} | SWITCH`, description: (c.opportunity || [DEFAULT_DESC])[0], keywords: [c.service, c.situation, c.sector, "growth consulting case study"].filter(Boolean).join(", ") };
      ogImage = abs(c.seoImage || c.image);
      crumbs.push({ name: "Our Work", path: "/our-work" }, { name: c.kicker || c.headline, path });
      ld.push(caseLd(c, `${SITE_URL}${path}`));
    }
  } else if (path !== "/" && PAGE_SEO[path]) {
    crumbs.push({ name: PAGE_SEO[path].title.split(":")[0].split(" | ")[0], path });
  }

  if (!meta) meta = { title: DEFAULT_TITLE, description: DEFAULT_DESC, keywords: DEFAULT_KEYWORDS };
  if (meta.image) ogImage = abs(meta.image);

  const canonical = `${SITE_URL}${path === "/" ? "" : path}`;
  const ogType = path.startsWith("/insights/") || path.startsWith("/our-work/") ? "article" : "website";

  if (path === "/how-we-work" || path === "/what-we-solve") ld.push(serviceLd);
  if (path === "/what-we-solve") {
    ld.push(faqLd(GROWTH_SITUATIONS.map((s) => ({
      q: `What does SWITCH do when ${s.title.toLowerCase()}?`,
      a: s.blurb,
    }))));
  }
  if (crumbs.length > 1) ld.push(breadcrumbLd(crumbs));

  return (
    <Helmet>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <meta name="keywords" content={meta.keywords || DEFAULT_KEYWORDS} />
      <link rel="canonical" href={canonical} />

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={meta.title} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={ogImage} />

      {ld.map((obj, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(obj)}</script>
      ))}
    </Helmet>
  );
};

export default AutoSeo;
